import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavigationContextType {
  referrer: string | null;
  setReferrer: (path: string | null) => void;
  getBackPath: () => string;
  navigateWithReferrer: (path: string, referrerPath?: string) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [referrer, setReferrerState] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Track referrer from location state (passed via navigate)
  useEffect(() => {
    const stateReferrer = (location.state as any)?.referrer;
    if (stateReferrer) {
      setReferrerState(stateReferrer);
    }
  }, [location.state]);

  const setReferrer = useCallback((path: string | null) => {
    setReferrerState(path);
  }, []);

  const getBackPath = useCallback((): string => {
    // Priority 1: Referrer from location state
    const stateReferrer = (location.state as any)?.referrer;
    if (stateReferrer) {
      return stateReferrer;
    }

    // Priority 2: Referrer from context
    if (referrer) {
      return referrer;
    }

    // Priority 3: Fallback based on current path
    const currentPath = location.pathname;
    
    if (currentPath.includes('/ombudsman/simulations/')) {
      return '/v3/ombudsman';
    }
    
    if (currentPath.includes('/signals/')) {
      return '/v4/records';
    }

    // Default fallback
    return '/v4/records';
  }, [location.state, location.pathname, referrer]);

  const navigateWithReferrer = useCallback((path: string, referrerPath?: string) => {
    const currentReferrer = referrerPath || location.pathname;
    navigate(path, { state: { referrer: currentReferrer } });
  }, [navigate, location.pathname]);

  return (
    <NavigationContext.Provider
      value={{
        referrer,
        setReferrer,
        getBackPath,
        navigateWithReferrer,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

